import { useState } from 'react';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';

/**
 * Displays one-time MFA recovery codes (shown exactly once, at enrollment or
 * regeneration) with copy + download affordances and a keep-them-safe warning.
 */
export function RecoveryCodesPanel({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked — the codes are still visible + downloadable
    }
  }

  function download() {
    const blob = new Blob([`Lizrd recovery codes\n\n${text}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lizrd-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <Alert variant="warning">
        Save these recovery codes somewhere safe. Each works once, and they&rsquo;re the only way
        back in if you lose your authenticator. You won&rsquo;t see them again.
      </Alert>
      <ul className="grid grid-cols-2 gap-2 rounded-md border border-hairline bg-surface-2 p-4">
        {codes.map((c) => (
          <li key={c} className="text-center font-mono text-sm tracking-wide text-bone">
            {c}
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={copy}>
          {copied ? 'Copied ✓' : 'Copy codes'}
        </Button>
        <Button variant="outline" size="sm" onClick={download}>
          Download .txt
        </Button>
      </div>
    </div>
  );
}
